import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { useEmpresas } from "providers/EmpresaProvider";
import { usePermissionModal } from "contexts/PermissionModalContext";
import {
  balancoService,
  BalancoType,
  CriarBalancoDto,
  IncluirProdutoBalancoDto,
  AtualizarProdutoBalancoDto,
  ExcluirProdutoBalancoDto,
  DepartamentoType,
  ZerarEstoqueDto
} from "services/balancoService";

// Query keys
export const balancoKeys = {
  all: ['balanco'] as const,
  ativo: () => [...balancoKeys.all, 'ativo'] as const,
  produtos: () => [...balancoKeys.all, 'produtos'] as const,
  produtosInfinito: (termo?: string) =>
    [...balancoKeys.produtos(), 'infinito', { termo }] as const,
  departamentos: () => [...balancoKeys.all, 'departamentos'] as const,
};

// Query para obter balanço ativo
export const useBalancoAtivo = () => {
  const { getQueryRetryHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0]; // Usa a primeira empresa selecionada

  return useQuery({
    queryKey: balancoKeys.ativo(),
    queryFn: () => balancoService.obterBalancoAtivo(empresaId),
    enabled: !!empresaId,
    retry: getQueryRetryHandler({
      queryKey: balancoKeys.ativo(),
      defaultMessage: "Você não possui permissão para consultar balanços."
    }),
  });
};

// Mutation para criar balanço
export const useCriarBalanco = () => {
  const queryClient = useQueryClient();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (dto: CriarBalancoDto) => balancoService.criarBalanco(dto, empresaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: balancoKeys.all });
    },
  });
};

// Mutation para incluir produto
export const useIncluirProduto = () => {
  const queryClient = useQueryClient();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (dto: IncluirProdutoBalancoDto) => balancoService.incluirProduto(dto, empresaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: balancoKeys.all });
    },
  });
};

// Mutation para atualizar produto
export const useAtualizarProduto = () => {
  const queryClient = useQueryClient();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (dto: AtualizarProdutoBalancoDto) => balancoService.atualizarProduto(dto, empresaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: balancoKeys.all });
    },
  });
};

// Mutation para excluir produto
export const useExcluirProduto = () => {
  const queryClient = useQueryClient();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (dto: ExcluirProdutoBalancoDto) => balancoService.excluirProduto(dto, empresaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: balancoKeys.all });
    },
  });
};

// Hook para pesquisar produtos com rolagem infinita
export const usePesquisarProdutosInfinito = (termo?: string) => {
  const { getQueryRetryHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useInfiniteQuery({
    queryKey: balancoKeys.produtosInfinito(termo),
    queryFn: ({ pageParam }) => balancoService.pesquisarProdutosInfinito(pageParam, termo, empresaId),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (!lastPage.isSuccess) return undefined;

      const currentPage = lastPage.pageIndex;
      const totalPages = Math.ceil(lastPage.total / 20); // pageSize = 20

      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    enabled: !!empresaId && !!termo,
    retry: getQueryRetryHandler({
      queryKey: balancoKeys.produtosInfinito(termo),
      defaultMessage: "Você não possui permissão para pesquisar produtos."
    }),
  });
};

// Mutation para finalizar balanço
export const useFinalizarBalanco = () => {
  const queryClient = useQueryClient();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (zerarEstoqueDto: ZerarEstoqueDto | null = null) =>
      balancoService.finalizarBalanco(zerarEstoqueDto, empresaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: balancoKeys.all });
    },
  });
};

// Query para obter departamentos
export const useDepartamentos = (enabled: boolean = true) => {
  const { getQueryRetryHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useQuery({
    queryKey: balancoKeys.departamentos(),
    queryFn: () => balancoService.obterDepartamentos(empresaId),
    enabled: !!empresaId && enabled,
    retry: getQueryRetryHandler({
      queryKey: balancoKeys.departamentos(),
      defaultMessage: "Você não possui permissão para consultar departamentos."
    }),
  });
};

